import React, { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { ROLES } from "../../config/supabase";
import DashboardLayout from "./DashboardLayout";
import { TeacherShellFrame } from "./TeacherShellLayout";
import { DashboardProfileProvider } from "../../contexts/DashboardProfileContext";

/**
 * Teachers: new shell (no sidebar, no grade header). Everyone else: classic dashboard layout.
 * If `user.profile` is missing from context, we refetch `profiles` once so teachers still get the new UI.
 *
 * We avoid a one-frame "old dashboard" flash by not rendering either layout until we know
 * the role (or know the profile row is missing after refetch).
 */
const TeacherOrDashboardLayout = ({ children }) => {
	const { user, loading, supabase } = useAuth();
	const [resolvedProfile, setResolvedProfile] = useState(null);
	/** idle = have profile from auth or no user; loading = refetching; done = refetch finished */
	const [profileFetchState, setProfileFetchState] = useState("idle");

	useEffect(() => {
		if (!user?.id || user.profile) {
			setResolvedProfile(null);
			setProfileFetchState("idle");
			return;
		}
		let cancelled = false;
		setProfileFetchState("loading");
		(async () => {
			try {
				const { data, error } = await supabase
					.from("profiles")
					.select("*")
					.eq("id", user.id)
					.single();
				if (!cancelled && !error && data) {
					setResolvedProfile(data);
				}
			} catch (e) {
				console.warn("TeacherOrDashboardLayout: profile refetch", e);
			} finally {
				if (!cancelled) {
					setProfileFetchState("done");
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [user?.id, user?.profile, supabase]);

	const effectiveProfile = useMemo(
		() => user?.profile ?? resolvedProfile ?? null,
		[user?.profile, resolvedProfile]
	);

	const role = effectiveProfile?.role;

	const isTeacher = role === ROLES.SCHOOL_TEACHER;

	const needsProfileResolution = Boolean(user?.id && !user.profile);
	const waitingProfile =
		needsProfileResolution &&
		(profileFetchState === "loading" || profileFetchState === "idle");

	if (loading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="100vh"
				sx={{ backgroundColor: "#f3f4f6" }}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (waitingProfile) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="100vh"
				sx={{ backgroundColor: "#f3f4f6" }}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	return (
		<DashboardProfileProvider value={effectiveProfile}>
			{isTeacher ? (
				<TeacherShellFrame>{children}</TeacherShellFrame>
			) : (
				<DashboardLayout>{children}</DashboardLayout>
			)}
		</DashboardProfileProvider>
	);
};

export default TeacherOrDashboardLayout;
